/*
 * Copyright © 2026 Bruce Matin,  ALL RIGHTS RESERVED!
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminService } from '../services/adminService';

interface User {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
  status: string;
}

interface Item {
  id: number;
  title: string;
  price: number;
  status: string;
  userId: number;
}

export default function AdminDashboard() {
  const [users, setUsers] = useState<User[]>([]);
  const [items, setItems] = useState<Item[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [usersRes, itemsRes] = await Promise.all([
        adminService.getUsers(),
        adminService.getItems()
      ]);
      setUsers(usersRes.data.data || []);
      setItems(itemsRes.data.data || []);
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Access Denied: Admin permissions required.');
      } else {
        setError('Failed to load admin data.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleApproveItem = async (itemId: number) => {
    try {
      await adminService.approveItem(itemId);
      fetchData();
    } catch (err) {
      alert('Failed to approve item.');
    }
  };

  const handleBanUser = async (userId: number) => {
    try {
      await adminService.banUser(userId);
      fetchData();
    } catch (err) {
      alert('Failed to ban user.');
    }
  };

  if (loading) return <div className="text-center mt-10 text-gray-600">Loading Admin Dashboard...</div>;
  if (error) return <div className="text-center mt-10 text-red-600">{error}</div>;

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <h1 className="text-3xl font-bold text-gray-900 mb-8">Admin Panel</h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
        {/* User Management */}
        <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
          <div className="p-6 border-b bg-gray-50">
            <h2 className="text-xl font-bold">User Management</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {users.map(user => (
                <tr key={user.id}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-bold">{user.firstName} {user.lastName}</div>
                    <div className="text-xs text-gray-500">{user.email}</div>
                  </td>
                  <td className="px-4 py-3 text-sm">{user.userType}</td>
                  <td className="px-4 py-3 text-right">
                    <button 
                      onClick={() => handleBanUser(user.id)}
                      className="text-red-600 hover:text-red-900 text-xs font-bold"
                    >
                      Ban
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Listing Management */}
        <div className="bg-white shadow rounded-lg overflow-hidden border border-gray-200">
          <div className="p-6 border-b bg-gray-50">
            <h2 className="text-xl font-bold">Listing Management</h2>
          </div>
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {items.map(item => (
                <tr key={item.id}>
                  <td className="px-4 py-3">
                    <div className="text-sm font-bold">{item.title}</div>
                    <div className="text-xs text-gray-500">${item.price}</div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-xs px-2 py-1 rounded ${item.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                      {item.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {item.status === 'Pending' && (
                      <button 
                        onClick={() => handleApproveItem(item.id)}
                        className="text-green-600 hover:text-green-900 text-xs font-bold"
                      >
                        Approve
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
